import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Card, Input, Select, Label, EmptyState, Button } from "../components/ui-kit";
import { EmailReportModal } from "../components/EmailReportModal";
import { MapPin, Users2, Route, Wallet, ChevronDown, ChevronUp, Briefcase, Home, Map, Download, Mail } from "lucide-react";
import { toast } from "sonner";
import { todayIST, fmtTimeIST, fmtDateIST } from "../lib/datefmt";

const MODE_LABEL = { field_day: "Field Day", meeting: "Meeting", work_from_home: "Work from Home" };
const MODE_STYLE = {
  field_day: "bg-[#1E3F20]/10 text-[#1E3F20]",
  meeting: "bg-[#DDA15E]/25 text-[#8B5A24]",
  work_from_home: "bg-[#A3B18A]/30 text-[#3E5133]",
};
const ROLE_LABEL = { sales: "Sales", sales_manager: "Sales Manager", rm_and_above: "RM & Above" };
const KIND_LABEL = { dealer: "Dealer", farmer: "Farmer", new_lead: "New lead", new_farmer: "New farmer" };
const num = (n) => Number(n || 0).toLocaleString("en-IN");
const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const daysAgoIST = (n) => {
  const [y, m, d] = todayIST().split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d - n));
  return dt.toISOString().slice(0, 10);
};

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#1E3F20]/10 grid place-items-center text-[#1E3F20]">
          <Icon className="w-5 h-5" strokeWidth={2.25} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#5C6B5E]">{label}</div>
          <div className="font-heading text-2xl font-black text-[#1A2E1C] leading-tight mt-1">{value}</div>
          {sub && <div className="text-xs text-[#5C6B5E] mt-0.5">{sub}</div>}
        </div>
      </div>
    </Card>
  );
}

function MemberOptions({ members }) {
  // Group: each team lead followed by their (indented) direct reports; orphans listed at the end.
  const leads = members.filter((m) => m.role !== "sales");
  const leadIds = new Set(leads.map((l) => l.id));
  const orphans = members.filter((m) => m.role === "sales" && !leadIds.has(m.reports_to));
  return (
    <>
      <option value="">Whole team</option>
      {leads.map((l) => (
        <optgroup key={l.id} label={`${l.name} · ${ROLE_LABEL[l.role] || l.role}`}>
          <option value={l.id}>{l.name} (incl. team)</option>
          {members
            .filter((m) => m.reports_to === l.id && m.role === "sales")
            .map((m) => (
              <option key={m.id} value={m.id}>
                &nbsp;&nbsp;↳ {m.name}
              </option>
            ))}
        </optgroup>
      ))}
      {orphans.length > 0 && (
        <optgroup label="Sales personnel">
          {orphans.map((m) => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </optgroup>
      )}
    </>
  );
}

function GeoPoint({ lat, lng, label }) {
  if (lat == null || lng == null) return <div className="text-[#5C6B5E]">{label}: —</div>;
  const la = Number(lat).toFixed(5);
  const ln = Number(lng).toFixed(5);
  return (
    <div>
      <span className="text-[#5C6B5E]">{label}:</span>{" "}
      <a
        href={`https://www.google.com/maps?q=${la},${ln}`}
        target="_blank"
        rel="noreferrer"
        className="font-mono text-[#1E3F20] underline decoration-dotted hover:text-[#2C5530]"
        title="Open in Google Maps"
      >
        {la}, {ln}
      </a>
    </div>
  );
}

function SessionRow({ s }) {
  const [open, setOpen] = useState(false);
  const mode = s.mode || "field_day";
  const isField = mode === "field_day";
  return (
    <>
      <tr className="border-t border-[#E5E2DC] hover:bg-[#F9F8F6]" data-testid={`review-session-${s.id}`}>
        <td className="px-4 py-3 whitespace-nowrap font-semibold">{s.date_key || fmtDateIST(s.start_time)}</td>
        <td className="px-4 py-3">
          <div className="font-semibold">{s.user_name}</div>
          <div className="text-[11px] uppercase tracking-wider text-[#5C6B5E]">{ROLE_LABEL[s.user_role] || s.user_role}</div>
          {s.working_with_user_name && <div className="text-[11px] text-[#5C6B5E]">with {s.working_with_user_name}</div>}
        </td>
        <td className="px-4 py-3">
          <span className={`inline-flex text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${MODE_STYLE[mode]}`}>
            {MODE_LABEL[mode]}
          </span>
          {s.status === "active" && <div className="text-[11px] text-[#8B5A24] font-semibold mt-1">In progress</div>}
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-sm">
          {fmtTimeIST(s.start_time)}{s.end_time ? ` – ${fmtTimeIST(s.end_time)}` : ""}
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-xs" data-testid={`review-startpoint-${s.id}`}>
          <GeoPoint lat={s.start_lat} lng={s.start_lng} label="Start" />
          <GeoPoint lat={s.end_lat} lng={s.end_lng} label="End" />
        </td>
        <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
          {isField ? (
            <>
              <div className="font-semibold">{num(s.km_covered)} km</div>
              <div className="text-[11px] text-[#5C6B5E]">{s.start_km ?? "—"} → {s.end_km ?? "—"}</div>
            </>
          ) : "—"}
        </td>
        <td className="px-4 py-3 text-right font-semibold">{isField ? s.visit_count : "—"}</td>
        <td className="px-4 py-3 text-right whitespace-nowrap">{s.expense_total ? money(s.expense_total) : "—"}</td>
        <td className="px-4 py-3 text-right whitespace-nowrap">
          <div className="inline-flex items-center gap-1">
            {isField && (
              <Link
                to={`/daily-report?session=${s.id}`}
                className="text-xs px-2.5 py-1 rounded bg-[#1E3F20] text-white hover:bg-[#152D17] font-semibold inline-flex items-center gap-1"
                data-testid={`review-route-${s.id}`}
              >
                <Map className="w-3.5 h-3.5" /> Route
              </Link>
            )}
            <button
              onClick={() => setOpen((v) => !v)}
              className="text-xs px-2.5 py-1 rounded border border-[#E5E2DC] hover:bg-[#F2EDE4] font-semibold inline-flex items-center gap-1"
              data-testid={`review-toggle-${s.id}`}
            >
              {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />} Details
            </button>
          </div>
        </td>
      </tr>
      {open && (
        <tr className="bg-[#F9F8F6]" data-testid={`review-details-${s.id}`}>
          <td colSpan={9} className="px-6 py-4">
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#5C6B5E] mb-1">
                  {mode === "work_from_home" ? "Achievement" : "Remarks"}
                </div>
                <div className="text-[#1A2E1C] whitespace-pre-wrap">
                  {(mode === "work_from_home" ? s.achievement : s.remarks) || s.end_remarks || <span className="text-[#5C6B5E]">—</span>}
                </div>
                {s.expense_total > 0 && (
                  <div className="mt-3 text-xs text-[#5C6B5E]">
                    Travel {money(s.travel_amount)} · DA {money(s.daily_allowance)} · Other {money((s.night_stay_amount || 0) + (s.other_amount || 0))}
                    {s.expense_status && <> · <span className="uppercase font-semibold">{s.expense_status}</span></>}
                  </div>
                )}
              </div>
              {isField && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#5C6B5E] mb-1">Visits ({s.visit_count})</div>
                  {s.visits?.length ? (
                    <ul className="space-y-1.5">
                      {s.visits.map((v, i) => (
                        <li key={i} className="flex gap-2">
                          <span className="text-[#5C6B5E] whitespace-nowrap">{fmtTimeIST(v.timestamp)}</span>
                          <span>
                            <b>{v.linked_name || KIND_LABEL[v.visit_kind] || "Visit"}</b>
                            <span className="text-[#5C6B5E]"> · {KIND_LABEL[v.visit_kind] || v.visit_kind}</span>
                            {v.remarks && <div className="text-[#5C6B5E] text-xs">{v.remarks}</div>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-[#5C6B5E]">No visits logged.</div>
                  )}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function TeamDailyReportPage() {
  const [members, setMembers] = useState([]);
  const [userId, setUserId] = useState("");
  const [from, setFrom] = useState(() => daysAgoIST(6));
  const [to, setTo] = useState(() => todayIST());
  const [data, setData] = useState({ sessions: [], summary: null, member_count: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/reviews/team-members").then((r) => setMembers(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (userId) params.set("user_id", userId);
        if (from) params.set("from", from);
        if (to) params.set("to", to);
        const r = await api.get(`/reviews/daily-reports?${params.toString()}`);
        if (!cancelled) setData(r.data);
      } catch (err) {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [userId, from, to]);

  const selected = useMemo(() => members.find((m) => m.id === userId), [members, userId]);
  const s = data.summary;
  const [emailOpen, setEmailOpen] = useState(false);
  const filters = { user_id: userId || null, from, to };
  const scopeLabel = selected ? selected.name : "Whole team";

  const downloadExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (userId) params.set("user_id", userId);
      params.set("from", from);
      params.set("to", to);
      const res = await api.get(`/reviews/daily-reports/export.xlsx?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `team_daily_report_${from}_to_${to}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <div data-testid="team-daily-report-page">
      <PageHeader
        title="Team Daily Report"
        subtitle="Review field days, meetings and work-from-home logs across your hierarchy — by person and date"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={downloadExcel} data-testid="review-download-xlsx"><Download className="w-4 h-4" /> Excel</Button>
            <Button onClick={() => setEmailOpen(true)} data-testid="review-email-btn"><Mail className="w-4 h-4" /> Email report</Button>
          </div>
        }
      />
      {emailOpen && <EmailReportModal filters={filters} scopeLabel={scopeLabel} onClose={() => setEmailOpen(false)} />}

      <Card className="p-4 mb-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="lg:col-span-2">
            <Label>Sales person / Sales manager</Label>
            <Select value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="review-member-select">
              <MemberOptions members={members} />
            </Select>
          </div>
          <div>
            <Label>From</Label>
            <Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} data-testid="review-from-date" />
          </div>
          <div>
            <Label>To</Label>
            <Input type="date" value={to} min={from} max={todayIST()} onChange={(e) => setTo(e.target.value)} data-testid="review-to-date" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {[["Today", 0], ["Last 7 days", 6], ["Last 30 days", 29]].map(([label, n]) => (
            <button
              key={label}
              onClick={() => { setFrom(daysAgoIST(n)); setTo(todayIST()); }}
              className="text-xs px-3 py-1.5 rounded-full border border-[#E5E2DC] hover:bg-[#F2EDE4] font-semibold"
              data-testid={`review-quick-${n}`}
            >
              {label}
            </button>
          ))}
        </div>
      </Card>

      {s && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <Kpi icon={Users2} label="People" value={num(data.member_count)} sub={selected ? selected.name : "whole team"} testid="review-kpi-people" />
          <Kpi icon={Briefcase} label="Field days" value={num(s.field_days)} sub={`${s.meetings} meetings · ${s.wfh} WFH`} testid="review-kpi-field-days" />
          <Kpi icon={Route} label="KM covered" value={num(s.km_covered)} testid="review-kpi-km" />
          <Kpi icon={MapPin} label="Visits" value={num(s.visits)} testid="review-kpi-visits" />
          <Kpi icon={Wallet} label="Expenses" value={money(s.expense_total)} testid="review-kpi-expenses" />
        </div>
      )}

      <Card className="overflow-hidden">
        {loading && <div className="p-4 text-sm text-[#5C6B5E]">Loading…</div>}
        {!loading && data.sessions.length === 0 ? (
          <div className="p-8">
            <EmptyState title="No daily reports" description="Nobody in this selection logged a day in the chosen date range." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="review-sessions-table">
              <thead className="bg-[#F9F8F6] text-[10px] uppercase tracking-widest text-[#5C6B5E]">
                <tr>
                  <th className="px-4 py-3 text-left">Date</th>
                  <th className="px-4 py-3 text-left">Person</th>
                  <th className="px-4 py-3 text-left">Mode</th>
                  <th className="px-4 py-3 text-left">Time (IST)</th>
                  <th className="px-4 py-3 text-left">Start / End point (lat, lng)</th>
                  <th className="px-4 py-3 text-right">KM</th>
                  <th className="px-4 py-3 text-right">Visits</th>
                  <th className="px-4 py-3 text-right">Expense</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {data.sessions.map((row) => <SessionRow key={row.id} s={row} />)}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <div className="text-xs text-[#5C6B5E] mt-3 inline-flex items-center gap-1">
        <Home className="w-3.5 h-3.5" /> Pick a manager to include their whole team; pick a sales person for just their days.
      </div>
    </div>
  );
}
